import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import PropTypes from '../../../node_modules/prop-types/index.js';
import Errors from '../Errors/Errors.js';
import Icon from '../Icon/Icon.js';
import TextInput from '../TextInput/index.js';
import i18n from '../../lang/i18n/index.js';
import { hasErrors, noop } from '../../utils/functions.js';
import handle from '../../utils/handle.js';
import safeCustomStyle from '../../utils/safeCustomStyle.js';
import dayjs from '../../utils/dayjs.js';
import { KEY_CODE } from '../constants.js';
import { FOCUS_ELEMENT, DATE_PART } from './constants.js';
import MonthToggle from './MonthToggle.js';
import { SmallText, Root, InputAndToggle, TogglePicker, Picker, Header, Table } from './styledComponents.js';
import Td from './Td.js';
import { dateToObj, genDaysOfMonth, getWeekdays, getMonths, shiftDate, isDisabledDay, getPrevElement, getNextElement, getErrors } from './utils.js';
import useDerivedState from '../../hooks/useDerivedState.js';
import useId from '../../hooks/useId.js';
import useLang from '../../hooks/useLang.js';
import useTheme from '../../hooks/useTheme.js';
import useOnClickOutside from '../../hooks/useOnClickOutside.js';
import componentWithName from '../../prop-types/componentWithName.js';

var WEEKS_FIVE = [0, 1, 2, 3, 4];
var WEEKS_SIX = [].concat(WEEKS_FIVE, [5]);

var DatePicker = function DatePicker(_ref) {
  var controlledId = _ref.id,
      isAutoPop = _ref.isAutoPop,
      required = _ref.required,
      dateFormat = _ref.dateFormat,
      labelFormat = _ref.labelFormat,
      value = _ref.value,
      disabled = _ref.disabled,
      disabledDays = _ref.disabledDays,
      errors = _ref.errors,
      errorCallout = _ref.errorCallout,
      onError = _ref.onError,
      dateInvalidError = _ref.dateInvalidError,
      dateRangeError = _ref.dateRangeError,
      label = _ref.label,
      nextMonthTitle = _ref.nextMonthTitle,
      prevMonthTitle = _ref.prevMonthTitle,
      togglePickerTitle = _ref.togglePickerTitle,
      tooltip = _ref.tooltip,
      _onChange = _ref.onChange,
      customStyle = _ref.customStyle,
      textBoxAriaLabel = _ref.textBoxAriaLabel,
      toggleAriaLabel = _ref.toggleAriaLabel,
      calendarTableAriaLabel = _ref.calendarTableAriaLabel,
      shouldCloseWithEscapeKey = _ref.shouldCloseWithEscapeKey;

  var _useDerivedState = useDerivedState(useId('datepicker-id-'), controlledId),
      _useDerivedState2 = _slicedToArray(_useDerivedState, 1),
      id = _useDerivedState2[0];

  var lang = useLang();
  var theme = useTheme();

  var _safeCustomStyle = safeCustomStyle(customStyle),
      rootStyle = _safeCustomStyle.rootStyle,
      pickerStyle = _safeCustomStyle.pickerStyle;

  var before = disabledDays.before,
      after = disabledDays.after;

  var _useState = useState(isAutoPop),
      _useState2 = _slicedToArray(_useState, 2),
      isPickerOpen = _useState2[0],
      setPickerOpen = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isMonthToggle = _useState4[0],
      setMonthToggle = _useState4[1];

  var _useState5 = useState(),
      _useState6 = _slicedToArray(_useState5, 2),
      inputError = _useState6[0],
      setInputError = _useState6[1];

  var _useState7 = useState(FOCUS_ELEMENT.DAY),
      _useState8 = _slicedToArray(_useState7, 2),
      focusedElement = _useState8[0],
      setFocusedElement = _useState8[1];

  var _useState9 = useState(null),
      _useState10 = _slicedToArray(_useState9, 2),
      focusedDayRef = _useState10[0],
      setFocusedDayRef = _useState10[1];

  var outsideClickTarget = useRef();
  var togglePickerRef = useRef();
  var prevMonthRef = useRef();
  var nextMonthRef = useRef();
  useOnClickOutside(outsideClickTarget, function () {
    return setPickerOpen(false);
  });
  var parsedValue = useMemo(function () {
    return value ? dayjs(value, dateFormat) : dayjs();
  }, [value, dateFormat]);
  useEffect(function () {
    if (!isPickerOpen) {
      setMonthToggle(false);
    }
  }, [isPickerOpen]);
  var calendarIcon = /*#__PURE__*/React.createElement(Icon, {
    manulifeName: "calendar",
    fill: disabled ? theme.colorPalette.neutralColors.light.shade_dark_3 : theme.colorPalette.neutralColors.dark.shade_light_1
  });
  var closeIcon = /*#__PURE__*/React.createElement(Icon, {
    manulifeName: "close",
    fill: theme.colorPalette.neutralColors.dark.shade_light_1
  });

  var _useState11 = useState(parsedValue ? {
    year: parsedValue.year(),
    month: parsedValue.month(),
    date: parsedValue.date()
  } : null),
      _useState12 = _slicedToArray(_useState11, 2),
      selectedDate = _useState12[0],
      setSelectedDate = _useState12[1];

  var _useState13 = useState(selectedDate ? selectedDate : dateToObj(new Date())),
      _useState14 = _slicedToArray(_useState13, 2),
      focusedDate = _useState14[0],
      setFocusedDate = _useState14[1];

  var year = focusedDate.year,
      month = focusedDate.month,
      date = focusedDate.date;
  var getDaysOfMonth = useCallback(function () {
    return genDaysOfMonth(year, month, disabledDays);
  }, [year, month, disabledDays]);
  var daysOfMonth = getDaysOfMonth(year, month, disabledDays);
  var weeks = daysOfMonth.length > 35 ? WEEKS_SIX : WEEKS_FIVE; // get memoized and localized text

  var daysOfWeek = useMemo(function () {
    return getWeekdays(lang);
  }, [lang]);
  var months = useMemo(function () {
    return getMonths(lang);
  }, [lang]);
  var toggleButtonTitle = useMemo(function () {
    return togglePickerTitle || i18n[lang].datePicker.toggleButtonTitle;
  }, [lang, togglePickerTitle]);
  /**
   * Shifts date selector (focusedDate) by offset. Updates focusedDate by calling `setFocusedDate`
   * @param {number} offset - # of units to shift date by
   * @param {string} type - unit of offset (year, month, day)
   * @param {bool} isFirstOfMonth - if shift causes a month change, set day to 1
   * @param {bool} monthToggle - if month changes when the monthToggle button is clicked, set `isMonthToggle` to true
   */

  var shift = useCallback(function (offset) {
    var type = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DATE_PART.DAY;
    var isFirstOfMonth = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    var monthToggle = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
    var requestedDate = shiftDate(focusedDate, offset, type, isFirstOfMonth);
    setMonthToggle(monthToggle);

    if (!isDisabledDay(requestedDate, before, after)) {
      setFocusedDate(dateToObj(requestedDate));
    } else {
      // if we're moving into disabled date territory use the last good date in that direction
      setFocusedDate(dateToObj(offset >= 0 ? after : before));
    }
  }, [focusedDate, before, after]);
  /**
   * Selects date by updating internal text value (setValue), date in frame (setFocusedDate)
   * as well as calling the onChange callback.
   * Picker is closed on select & text input is focused.
   * @param {Date} newDate - JS Date to select
   */

  var selectDate = useCallback(function (event, inYear, inMonth, inDate) {
    var day = dayjs([inYear, inMonth, inDate]);
    var formattedDate = day.format(dateFormat);
    var oldDate = selectedDate ? dayjs(selectedDate).format(dateFormat) : '';

    if (oldDate !== formattedDate || oldDate !== value) {
      setFocusedDate({
        year: inYear,
        month: inMonth,
        date: inDate
      });
      setSelectedDate({
        year: inYear,
        month: inMonth,
        date: inDate
      });
      setInputError();
      handle(_onChange)(event, formattedDate);
    }

    setPickerOpen(false);
  }, [dateFormat, _onChange, selectedDate, value]);
  var deselectDate = useCallback(function (event) {
    setInputError();
    setFocusedDate(dateToObj(new Date()));
    setSelectedDate(null);
    handle(_onChange)(event, '');
  }, [_onChange]); // Deselect in the picker when the value/state becomes empty.

  useEffect(function () {
    if (value === '') {
      setFocusedDate(dateToObj(new Date()));
      setSelectedDate(null);
    }
  }, [value, deselectDate]);
  var validateSelectedDate = useCallback(function (parsedDate) {
    if (!parsedDate || !parsedDate.isValid()) {
      setInputError(dateInvalidError || i18n[lang].datePicker.dateFormatInvalid);
      return false;
    }

    if (isDisabledDay(parsedDate.toDate(), before, after)) {
      setInputError(dateRangeError || i18n[lang].datePicker.dateOutsideRangeTemplate({
        before: before && dayjs(before).format(dateFormat),
        after: after && dayjs(after).format(dateFormat)
      }));
      return false;
    }

    return true;
  }, [dateFormat, after, before, lang, dateInvalidError, dateRangeError]);
  var onBlurHandler = useCallback(function (newValue, event) {
    var oldDate = selectedDate ? dayjs(selectedDate, dateFormat, true) : '';
    var parsedDate = newValue ? dayjs(newValue, dateFormat, true) : '';

    if (newValue === oldDate && !required && newValue !== '') {
      // value hasn't changed - either clicking on input and focus on picker first time, or input cleared
      return handle(onError)(event, newValue);
    }

    if ((parsedDate || required) && !validateSelectedDate(parsedDate)) {
      return handle(onError)(event, newValue);
    }

    if (newValue) {
      return selectDate(event, parsedDate.year(), parsedDate.month(), parsedDate.date());
    } // new value is blank so this is an attempt to clear the date (but a value was selected previously)


    return deselectDate(event);
  }, [selectDate, deselectDate, dateFormat, onError, selectedDate, required, validateSelectedDate]);
  var handleESCKeyPress = useCallback(function (event) {
    if (event.keyCode === KEY_CODE.ESCAPE) {
      setPickerOpen(false);
    }
  }, []);
  useEffect(function () {
    if (shouldCloseWithEscapeKey) {
      window.addEventListener('keydown', handleESCKeyPress);
      return function () {
        window.removeEventListener('keydown', handleESCKeyPress);
      };
    }

    return function () {
      return null;
    };
  }, [handleESCKeyPress, shouldCloseWithEscapeKey]);
  var tabKeyHandler = useCallback(function (event) {
    var TOGGLE_PICKER = FOCUS_ELEMENT.TOGGLE_PICKER,
        PREV_MONTH = FOCUS_ELEMENT.PREV_MONTH,
        NEXT_MONTH = FOCUS_ELEMENT.NEXT_MONTH,
        DAY = FOCUS_ELEMENT.DAY;
    var focusableElements = {};
    focusableElements[TOGGLE_PICKER] = togglePickerRef;
    focusableElements[PREV_MONTH] = prevMonthRef;
    focusableElements[NEXT_MONTH] = nextMonthRef;
    focusableElements[DAY] = focusedDayRef;

    if (isPickerOpen && event.keyCode === KEY_CODE.TAB) {
      var prevMonthDisabled = focusableElements[PREV_MONTH].current.disabled;
      var nextMonthDisabled = focusableElements[NEXT_MONTH].current.disabled;
      var element;

      if (event.shiftKey) {
        element = getPrevElement(focusedElement, prevMonthDisabled, nextMonthDisabled);
      }

      if (!event.shiftKey) {
        element = getNextElement(focusedElement, prevMonthDisabled, nextMonthDisabled);
      }

      focusableElements[element].current.focus();
      setFocusedElement(element);
      return event.preventDefault();
    }

    return function () {
      return null;
    };
  }, [focusedElement, focusedDayRef, isPickerOpen]);
  useEffect(function () {
    if (!isPickerOpen) {
      setFocusedElement(FOCUS_ELEMENT.DAY);
    }
  }, [isPickerOpen]);
  var arrowKeyHandler = useCallback(function (event) {
    var key = event.key;

    switch (key) {
      case 'ArrowLeft':
        {
          event.preventDefault();
          shift(-1);
          break;
        }

      case 'ArrowRight':
        {
          event.preventDefault();
          shift(+1);
          break;
        }

      case 'ArrowUp':
        {
          event.preventDefault();
          shift(-7);
          break;
        }

      case 'ArrowDown':
        {
          event.preventDefault();
          shift(+7);
          break;
        }

      case 'Enter':
      case ' ':
        {
          event.preventDefault();
          selectDate(event, year, month, date);
          break;
        }
    }
  }, [year, month, date, selectDate, shift]);
  var inputId = "".concat(id, "-input");
  var dateFormatLabel = "(".concat(dateFormat.toLowerCase().replace(/d/gi, i18n[lang].global.dateAbbr.day).replace(/m/gi, i18n[lang].global.dateAbbr.month).replace(/y/gi, i18n[lang].global.dateAbbr.year), ")");
  var formattedLabel = /*#__PURE__*/React.createElement("span", null, label, "\xA0", (labelFormat || dateFormat) && /*#__PURE__*/React.createElement(SmallText, {
    theme: theme
  }, (labelFormat || dateFormatLabel).toLowerCase()));
  var inputErrors = useMemo(function () {
    return getErrors(errors, inputError);
  }, [errors, inputError]);
  return /*#__PURE__*/React.createElement(Root, {
    theme: theme,
    rootStyle: rootStyle
  }, /*#__PURE__*/React.createElement(InputAndToggle, null, /*#__PURE__*/React.createElement(TextInput, {
    id: inputId,
    value: value,
    disabled: disabled,
    onBlur: onBlurHandler,
    onChange: function onChange(newValue, event) {
      setInputError();
      handle(_onChange)(event, newValue);
    },
    onKeyDown: useCallback(function (e) {
      var key = e.key;

      if (key === 'Enter') {
        setPickerOpen(true);
      }
    }, []),
    "aria-haspopup": true,
    mask: dateFormat.replace(/[dmy]{1}/gi, '9'),
    "aria-controls": "".concat(id, "-input"),
    ariaRequired: required,
    autoComplete: "off",
    customStyle: {
      rootStyle: {
        width: 'auto'
      }
    },
    label: formattedLabel,
    required: required,
    showOptionalLabel: !required,
    tooltip: tooltip,
    "aria-label": textBoxAriaLabel,
    outline: hasErrors(inputErrors)
  }), /*#__PURE__*/React.createElement(TogglePicker, {
    type: "button",
    onClick: function onClick() {
      return setPickerOpen(!isPickerOpen);
    },
    "aria-expanded": isPickerOpen,
    title: toggleButtonTitle,
    "data-testid": "".concat(id, "-toggle"),
    disabled: disabled,
    "aria-label": toggleAriaLabel,
    ref: togglePickerRef,
    onKeyDown: tabKeyHandler
  }, isPickerOpen ? closeIcon : calendarIcon)), /*#__PURE__*/React.createElement(Errors, {
    id: id,
    callouts: errorCallout,
    errors: inputErrors
  }), isPickerOpen && !disabled && /*#__PURE__*/React.createElement(Picker, {
    id: "".concat(id, "-picker"),
    "data-testid": "".concat(id, "-picker"),
    ref: outsideClickTarget,
    theme: theme,
    pickerStyle: pickerStyle
  }, /*#__PURE__*/React.createElement(Header, null, /*#__PURE__*/React.createElement(MonthToggle, {
    title: prevMonthTitle,
    inc: -1,
    disabledDays: disabledDays,
    months: months,
    focusedDate: focusedDate,
    shift: shift,
    ref: prevMonthRef,
    tabKeyHandler: tabKeyHandler,
    setFocusedElement: setFocusedElement,
    focusedDayRef: focusedDayRef
  }), /*#__PURE__*/React.createElement("div", null, "".concat(months[month], " ").concat(year)), /*#__PURE__*/React.createElement(MonthToggle, {
    title: nextMonthTitle,
    inc: 1,
    disabledDays: disabledDays,
    months: months,
    focusedDate: focusedDate,
    shift: shift,
    ref: nextMonthRef,
    tabKeyHandler: tabKeyHandler,
    setFocusedElement: setFocusedElement,
    focusedDayRef: focusedDayRef
  })), /*#__PURE__*/React.createElement(Table, {
    role: "grid",
    ariaActiveDescendant: "cell-".concat(year, "-").concat(month, "-").concat(date),
    "aria-label": calendarTableAriaLabel
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, daysOfWeek.map(function (day) {
    var abbr = day.abbr,
        title = day.title;
    return /*#__PURE__*/React.createElement("th", {
      key: title,
      role: "columnheader",
      "aria-label": title
    }, /*#__PURE__*/React.createElement("abbr", {
      title: title
    }, abbr));
  }))), /*#__PURE__*/React.createElement("tbody", null, weeks.map(function (week) {
    return /*#__PURE__*/React.createElement("tr", {
      key: "".concat(week)
    }, daysOfMonth.slice(week * 7, (week + 1) * 7).map(function (day) {
      return /*#__PURE__*/React.createElement(Td, {
        key: "".concat(day.year, "-").concat(day.month, "-").concat(day.date),
        day: day,
        isFocusedDay: date === day.date && month === day.month && year === day.year,
        hasFocus: focusedElement === FOCUS_ELEMENT.DAY,
        isMonthToggle: isMonthToggle,
        selectedDate: selectedDate,
        isHidden: day.month !== month,
        selectDate: selectDate,
        setFocusedDayRef: setFocusedDayRef,
        arrowKeyHandler: arrowKeyHandler,
        tabKeyHandler: tabKeyHandler
      });
    }));
  })))));
};

process.env.NODE_ENV !== "production" ? DatePicker.reduxFormProps = {
  inputProp: 'value',
  inputType: 'string'
} : void 0;
process.env.NODE_ENV !== "production" ? DatePicker.propTypes = {
  id: PropTypes.string,

  /** the [day.js format](https://day.js.org/docs/en/display/format) to use when parsing / formatting dates.
   * Currently supports only 2- and 4- digit day/month/year specifiers. */
  dateFormat: PropTypes.string,

  /** the format displayed in the label */
  labelFormat: PropTypes.string,

  /** the string representation of the date value */
  value: PropTypes.string,

  /** boolean to disable datepicker * */
  disabled: PropTypes.bool,

  /** date range that should represent disabled dates */
  disabledDays: PropTypes.shape({
    /** dates before this date are disabled */
    before: PropTypes.instanceOf(Date),

    /** dates after this date are disabled */
    after: PropTypes.instanceOf(Date)
  }),

  /**
   * error messages to display
   */
  errors: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    msg: PropTypes.string,
    isError: PropTypes.bool.isRequired
  })),

  /** should the error messages be read out as an alert on a screen reader? */
  errorCallout: PropTypes.bool,

  /**
   * callback function when the provided date via text input is invalid.
   * The picker itself will not fire the onError function because invalid dates cannot be selected.
   */
  onError: PropTypes.func,

  /** error message for a date value not matching dateFormat */
  dateInvalidError: PropTypes.string,

  /** error message for a date value not between disabledDays.before and disabledDays.after */
  dateRangeError: PropTypes.string,

  /** label for datepicker text input */
  label: PropTypes.node.isRequired,

  /** change handler for DatePicker */
  onChange: PropTypes.func.isRequired,

  /** should the picker be popped on mount */
  isAutoPop: PropTypes.bool,

  /** should this field be required (displays 'Optional' label if not) */
  required: PropTypes.bool,

  /** title for next month button */
  nextMonthTitle: PropTypes.string,

  /** title for prev month button */
  prevMonthTitle: PropTypes.string,

  /** title for toggle datepicker button */
  togglePickerTitle: PropTypes.string,

  /** [Tooltip](/?path=/docs/components-tooltip) displayed beside the label */
  tooltip: componentWithName('Tooltip'),

  /** style overrides */
  customStyle: PropTypes.shape({
    rootStyle: PropTypes.object,
    pickerStyle: PropTypes.object
  }),

  /** aria-label for the date textbox */
  textBoxAriaLabel: PropTypes.string,

  /** aria-label for the toggle button */
  toggleAriaLabel: PropTypes.string,

  /** aria-label for the calendar */
  calendarTableAriaLabel: PropTypes.string,

  /** Indicate if pressing on escape key should close the picker or not */
  shouldCloseWithEscapeKey: PropTypes.bool
} : void 0;
DatePicker.defaultProps = {
  id: undefined,
  dateFormat: 'DD-MM-YYYY',
  labelFormat: undefined,
  value: '',
  disabled: false,
  disabledDays: {},
  errors: [],
  errorCallout: false,
  onError: noop,
  dateInvalidError: null,
  dateRangeError: null,
  isAutoPop: false,
  required: true,
  nextMonthTitle: '',
  prevMonthTitle: '',
  togglePickerTitle: '',
  tooltip: undefined,
  customStyle: {
    rootStyle: {},
    pickerStyle: {}
  },
  textBoxAriaLabel: null,
  toggleAriaLabel: null,
  calendarTableAriaLabel: null,
  shouldCloseWithEscapeKey: true
};
process.env.NODE_ENV !== "production" ? DatePicker.displayName = 'DatePicker' : void 0;
var DatePicker$1 = DatePicker;

export { DatePicker$1 as default };
